import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TrackingStore } from '@core/state/tracking/tracking.store';
import { EMPTY, catchError } from 'rxjs';
import { EnvironmentService } from '../environment/environment.service';

/**
 * Bridge service. SessionId state lives in TrackingStore (localStorage,
 * shared across tabs). HTTP fire-and-forget POST stays here.
 */
@Injectable({
  providedIn: 'root',
})
export class PageVisitTrackingService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(EnvironmentService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly trackingStore = inject(TrackingStore);

  private readonly API_URL = this.environment.getApiUrl();

  /**
   * Fire-and-forget page visit recording.
   * Subscribes immediately and swallows all errors so it never breaks navigation.
   */
  recordPageVisit(clientSlug: string, clientId: number, pagePath: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const sessionId = this.trackingStore.ensureSessionId();
    if (!sessionId) {
      return;
    }

    const referrer = typeof document !== 'undefined' ? document.referrer || null : null;
    const country = this.detectCountry();

    this.http
      .post(`${this.API_URL}v1/analytics/page-visits`, {
        client_id: clientId,
        client_slug: clientSlug,
        session_id: sessionId,
        page_path: pagePath,
        referrer: referrer,
        country: country,
      })
      .pipe(catchError(() => EMPTY))
      .subscribe();
  }

  private detectCountry(): string | null {
    const lang = navigator.language ?? navigator.languages?.[0];
    if (!lang) return null;
    const parts = lang.split('-');
    if (parts.length < 2) return null;
    const tag = parts[parts.length - 1];
    return tag.length === 2 ? tag.toUpperCase() : null;
  }
}
